// src/components/admin/AssessmentBuilder.jsx

import React, { useState, useEffect } from 'react';
import { 
    Plus, ListChecks, Save, Copy, LayoutTemplate, ArrowUp, ArrowDown, 
    Trash2, AlertTriangle, X, Loader2, Tag, Info, Brain // Added Brain icon
} from 'lucide-react';
import { db } from '../../config/firebase-config';
import { collection, doc, onSnapshot, setDoc } from 'firebase/firestore'; 
import { CLINICAL_MODULES, SCORING_METRICS } from '../../data/assessment-config'; 

const AssessmentBuilder = () => {
    const [configs, setConfigs] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [activeConfigId, setActiveConfigId] = useState(null); 
    const [isSaving, setIsSaving] = useState(false);

    const [formData, setFormData] = useState({
        id: '', title: '', audience: 'Universal',
        screener: [], modules: {},
        // NEW CONFIG FIELDS
        configOptions: {
            includeCognitive: true,
            cognitivePool: ['road_sign'] // Default pool
        }
    });

    useEffect(() => {
        const unsubConfigs = onSnapshot(collection(db, "assessment_configs"), (snap) => setConfigs(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubTemplates = onSnapshot(collection(db, "assessment_templates"), (snap) => setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        return () => { unsubConfigs(); unsubTemplates(); };
    }, []);

    const addSection = () => {
        setFormData(prev => ({ ...prev, screener: [...prev.screener, { id: `sec_${Date.now()}`, title: "New Section", description: "", questions: [] }] }));
    };

    const deleteSection = (idx) => {
        if (!window.confirm("Delete this entire section?")) return;
        const newScreener = [...formData.screener];
        newScreener.splice(idx, 1);
        setFormData({ ...formData, screener: newScreener });
    };

    const moveSection = (idx, direction) => {
        const newScreener = [...formData.screener];
        const targetIndex = idx + direction;
        if (targetIndex < 0 || targetIndex >= newScreener.length) return;
        [newScreener[idx], newScreener[targetIndex]] = [newScreener[targetIndex], newScreener[idx]];
        setFormData({ ...formData, screener: newScreener });
    };

    const updateSectionTitle = (idx, val) => {
        const newScreener = [...formData.screener];
        newScreener[idx].title = val;
        setFormData({ ...formData, screener: newScreener });
    };

    const addQuestion = (sIdx) => {
        const newScreener = [...formData.screener];
        newScreener[sIdx].questions.push({ 
            id: `q_${Date.now()}`, 
            text: "", 
            type: "slider_100", 
            options: [], 
            trigger: null, 
            metric: "",
            scaleLabels: "",
            useManualScoring: false,
            manualValues: {}
        });
        setFormData({ ...formData, screener: newScreener });
    };

    const deleteQuestion = (sIdx, qIdx) => {
        const newScreener = [...formData.screener];
        newScreener[sIdx].questions.splice(qIdx, 1);
        setFormData({ ...formData, screener: newScreener });
    };

    const updateQuestion = (sIdx, qIdx, field, value) => {
        const newScreener = [...formData.screener];
        newScreener[sIdx].questions[qIdx][field] = value;
        setFormData({ ...formData, screener: newScreener });
    };

    const addOption = (sIdx, qIdx) => {
        const newScreener = [...formData.screener];
        const q = newScreener[sIdx].questions[qIdx];
        if (!q.options) q.options = [];
        q.options.push(`Option ${q.options.length + 1}`);
        setFormData({ ...formData, screener: newScreener });
    };

    const updateOption = (sIdx, qIdx, optIdx, value) => {
        const newScreener = [...formData.screener];
        newScreener[sIdx].questions[qIdx].options[optIdx] = value;
        setFormData({ ...formData, screener: newScreener });
    };

    const removeOption = (sIdx, qIdx, optIdx) => {
        const newScreener = [...formData.screener];
        newScreener[sIdx].questions[qIdx].options.splice(optIdx, 1);
        setFormData({ ...formData, screener: newScreener });
    };

    const toggleTrigger = (sIdx, qIdx) => {
        const newScreener = [...formData.screener];
        const q = newScreener[sIdx].questions[qIdx];
        if (q.trigger) { 
            q.trigger = null; 
        } else { 
            q.trigger = { 
                threshold: 2, 
                operator: '>=', 
                module: Object.keys(CLINICAL_MODULES)[0] 
            }; 
        }
        setFormData({ ...formData, screener: newScreener });
    };

    const updateTrigger = (sIdx, qIdx, field, value) => {
        const newScreener = [...formData.screener];
        if (!newScreener[sIdx].questions[qIdx].trigger) return;
        newScreener[sIdx].questions[qIdx].trigger[field] = value;
        setFormData({ ...formData, screener: newScreener });
    };

    const handleSave = async () => {
        if (!formData.id || !formData.title) return alert("ID and Title required");
        setIsSaving(true);

        const sanitizedScreener = formData.screener.map(section => ({
            ...section,
            questions: section.questions.map(q => {
                const newQ = { ...q };
                if (typeof q.scaleLabels === 'string' && q.scaleLabels.trim() !== "") {
                    newQ.scaleLabels = q.scaleLabels.split(',').map(s => s.trim());
                } else if (q.scaleLabels === "") {
                    delete newQ.scaleLabels;
                }
                // Slider range labels: exactly four, or none (the engine default).
                if (typeof q.rangeLabels === 'string') {
                    const parts = q.rangeLabels.split(',').map(s => s.trim()).filter(Boolean);
                    if (q.type === 'slider_100' && parts.length === 4) newQ.rangeLabels = parts;
                    else delete newQ.rangeLabels;
                }
                return newQ;
            })
        }));

        try {
            await setDoc(doc(db, "assessment_configs", formData.id), { ...formData, screener: sanitizedScreener });
            alert("Assessment Saved!");
            setActiveConfigId(null);
        } catch (e) { alert("Save failed: " + e.message); }
        setIsSaving(false);
    };

    const handleSaveTemplate = async () => {
        const name = prompt("Name this template:");
        if (!name) return;
        setIsSaving(true);
        try {
            const templateId = `tpl_${Date.now()}`;
            await setDoc(doc(db, "assessment_templates", templateId), { 
                ...formData, id: templateId, title: name, isTemplate: true 
            });
            alert("Template Saved!");
        } catch (e) { alert("Template save failed: " + e.message); }
        setIsSaving(false);
    };

    const loadConfig = (config) => { 
        const editableScreener = (config.screener || []).map(sec => ({
            ...sec,
            questions: (sec.questions || []).map(q => ({
                ...q,
                scaleLabels: Array.isArray(q.scaleLabels) ? q.scaleLabels.join(', ') : (q.scaleLabels || ""),
                rangeLabels: Array.isArray(q.rangeLabels) ? q.rangeLabels.join(', ') : (q.rangeLabels || "")
            }))
        }));
        // Ensure configOptions exist when loading
        const loadedConfig = {
            ...config,
            screener: editableScreener,
            configOptions: config.configOptions || { includeCognitive: true, cognitivePool: ['road_sign'] }
        };
        setFormData(loadedConfig); 
        setActiveConfigId(config.id); 
    };

    const loadTemplate = (template) => { 
        setFormData({ 
            ...template, 
            id: `assess_${Date.now()}`, 
            title: `Copy of ${template.title}`, 
            isTemplate: false,
            configOptions: template.configOptions || { includeCognitive: true, cognitivePool: ['road_sign'] }
        }); 
        setActiveConfigId('new'); 
    };
    
    const startNew = () => {
        setFormData({
            id: `assess_${Date.now()}`, title: '', audience: 'Universal',
            screener: [ { id: 'sec_prescreen', title: '1. Pre-Screen', description: 'Context & Health', questions: [] } ],
            modules: {},
            configOptions: { includeCognitive: true, cognitivePool: ['road_sign'] }
        });
        setActiveConfigId('new');
    };

    if (activeConfigId) {
        return (
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 animate-in slide-in-from-right-4">
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-100">
                    <div>
                        <h2 className="text-2xl font-black text-slate-800">Assessment Builder</h2>
                        <p className="text-slate-500 text-sm">Editing: <span className="font-mono text-brand-primary">{formData.title || 'New Assessment'}</span></p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setActiveConfigId(null)} className="px-4 py-2 text-slate-500 font-bold hover:bg-slate-100 rounded-lg">Cancel</button>
                        <button onClick={handleSaveTemplate} disabled={isSaving} className="px-4 py-2 text-brand-primary font-bold hover:bg-blue-50 border border-brand-primary/20 rounded-lg flex items-center gap-2"><Copy size={16}/> Save Template</button>
                        <button onClick={handleSave} disabled={isSaving} className="px-6 py-2 bg-slate-900 text-white font-bold rounded-lg hover:bg-black flex items-center gap-2">{isSaving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>} Save Config</button>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                    <div><label className="block text-xs font-bold text-slate-400 uppercase mb-1">Internal ID (Unique)</label><input className="w-full p-2 border border-slate-200 rounded-lg font-mono text-sm" value={formData.id} onChange={e => setFormData({...formData, id: e.target.value})} /></div>
                    <div><label className="block text-xs font-bold text-slate-400 uppercase mb-1">Display Title</label><input className="w-full p-2 border border-slate-200 rounded-lg" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} /></div>
                </div>

                {/* --- ADVANCED CONFIGURATION --- */}
                <div className="mb-8 p-5 bg-slate-50 rounded-xl border border-slate-200 flex flex-col gap-4">
                    <h3 className="text-sm font-black text-slate-800 uppercase flex items-center gap-2 mb-1">
                        <Brain size={18} className="text-brand-primary" /> Advanced Features
                    </h3>
                    
                    {/* Cognitive Toggle */}
                    <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                                type="checkbox" 
                                className="w-4 h-4 rounded border-slate-300 text-brand-primary focus:ring-brand-primary"
                                checked={formData.configOptions?.includeCognitive !== false} // Defaults to true
                                onChange={(e) => setFormData({
                                    ...formData, 
                                    configOptions: { ...formData.configOptions, includeCognitive: e.target.checked }
                                })}
                            />
                            <span className="text-sm font-bold text-slate-700">Include Cognitive Tests (Road Sign)</span>
                        </label>
                        {!formData.configOptions?.includeCognitive && (
                            <p className="text-[11px] text-orange-600 font-bold italic flex items-center gap-1">
                                <AlertTriangle size={12}/> Cognitive testing disabled.
                            </p>
                        )}
                    </div>

                    {/* AI Chat Toggle */}
                    <div className="flex items-center gap-6 pt-3 border-t border-slate-200">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                                type="checkbox" 
                                className="w-4 h-4 rounded border-slate-300 text-brand-primary focus:ring-brand-primary"
                                checked={formData.configOptions?.includeChatAgent !== false} // Defaults to true
                                onChange={(e) => setFormData({
                                    ...formData, 
                                    configOptions: { ...formData.configOptions, includeChatAgent: e.target.checked }
                                })}
                            />
                            <span className="text-sm font-bold text-slate-700">Enable AI Debrief Chat (End of Assessment)</span>
                        </label>
                        {formData.configOptions?.includeChatAgent === false && (
                            <p className="text-[11px] text-slate-500 font-bold italic flex items-center gap-1">
                                <Info size={12}/> Users will skip the chat and go straight to completion.
                            </p>
                        )}
                    </div>
                </div>

                <div className="space-y-6">
                    {formData.screener.map((section, sIdx) => (
                        <div key={section.id} className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                            <div className="bg-slate-50 p-4 border-b border-slate-100 flex justify-between items-center">
                                <div className="flex gap-2 items-center flex-1">
                                    <div className="flex flex-col gap-1 mr-2">
                                        <button onClick={() => moveSection(sIdx, -1)} disabled={sIdx === 0} className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-brand-primary disabled:opacity-20"><ArrowUp size={12}/></button>
                                        <button onClick={() => moveSection(sIdx, 1)} disabled={sIdx === formData.screener.length - 1} className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-brand-primary disabled:opacity-20"><ArrowDown size={12}/></button>
                                    </div>
                                    <input className="bg-transparent font-bold text-slate-700 outline-none w-full" value={section.title} onChange={(e) => updateSectionTitle(sIdx, e.target.value)} placeholder="Section Title" />
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => addQuestion(sIdx)} className="text-xs bg-white border border-slate-200 px-3 py-1 rounded-md font-bold text-brand-primary hover:bg-blue-50">+ Add Question</button>
                                    <button onClick={() => deleteSection(sIdx)} className="text-slate-400 hover:text-red-500 p-1 hover:bg-red-50 rounded"><Trash2 size={16}/></button>
                                </div>
                            </div>
                            
                            <div className="p-4 space-y-6">
                                {section.questions.map((q, qIdx) => {
                                    const isScaledType = ['scale_frequency', 'scale_0_3', 'scale_0_4', 'scale_0_5'].includes(q.type);
                                    
                                    return (
                                        <div key={q.id} className="flex gap-4 items-start group border-b border-slate-100 pb-4 last:border-0 last:pb-0">
                                            <div className="flex-1 space-y-3">
                                                <div className="flex gap-2">
                                                    <input className="flex-1 p-2 border border-slate-200 rounded-lg text-sm font-medium" placeholder="Question Text..." value={q.text} onChange={(e) => updateQuestion(sIdx, qIdx, 'text', e.target.value)} />
                                                    <select className="p-2 border border-slate-200 rounded-lg text-sm bg-slate-50 font-bold text-slate-600" value={q.type} onChange={(e) => updateQuestion(sIdx, qIdx, 'type', e.target.value)}>
                                                        <option value="slider_100">Slider (0-100)</option>
                                                        <option value="scale_frequency">Frequency (0-3)</option>
                                                        <option value="scale_0_4">Frequency (0-4)</option>
                                                        <option value="scale_0_5">Frequency (0-5)</option>
                                                        <option value="yes_no">Yes / No</option>
                                                        <option value="text">Text Input</option>
                                                        <option value="select">Dropdown</option>
                                                        <option value="drag_sort">Ranking</option>
                                                    </select>
                                                    <button onClick={() => deleteQuestion(sIdx, qIdx)} className="text-slate-300 hover:text-red-500 p-2"><Trash2 size={16}/></button>
                                                </div>

                                                {isScaledType && (
                                                    <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100 ml-4 animate-in fade-in space-y-3">
                                                        <div>
                                                            <label className="flex items-center gap-1.5 text-xs font-bold text-blue-700 uppercase mb-1.5">
                                                                <Info size={12}/> Custom Scale Labels (Comma Separated)
                                                            </label>
                                                            <input 
                                                                className="w-full p-2 border border-blue-200 rounded bg-white text-xs" 
                                                                placeholder={`e.g. Never, Rarely, Sometimes, Always`}
                                                                value={q.scaleLabels || ""}
                                                                onChange={(e) => updateQuestion(sIdx, qIdx, 'scaleLabels', e.target.value)}
                                                            />
                                                        </div>

                                                        <div className="pt-2 border-t border-blue-100">
                                                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                                                                <input 
                                                                    type="checkbox" 
                                                                    checked={!!q.useManualScoring} 
                                                                    onChange={(e) => updateQuestion(sIdx, qIdx, 'useManualScoring', e.target.checked)}
                                                                />
                                                                <span className="text-[10px] font-black text-blue-600 uppercase">Enable Manual Point Values</span>
                                                            </label>

                                                            {q.useManualScoring && q.scaleLabels && (
                                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                                                                    {q.scaleLabels.split(',').map((label, lIdx) => (
                                                                        <div key={lIdx} className="flex flex-col gap-1">
                                                                            <span className="text-[9px] text-slate-500 truncate font-bold">{label.trim() || `Option ${lIdx}`}</span>
                                                                            <input 
                                                                                type="number"
                                                                                className="p-1 text-xs border border-blue-200 rounded text-center"
                                                                                placeholder="Pts"
                                                                                value={q.manualValues?.[label.trim()] || 0}
                                                                                onChange={(e) => {
                                                                                    const newVals = { ...(q.manualValues || {}) };
                                                                                    newVals[label.trim()] = parseInt(e.target.value) || 0;
                                                                                    updateQuestion(sIdx, qIdx, 'manualValues', newVals);
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {q.type === 'slider_100' && (
                                                    <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100 ml-4">
                                                        <label className="flex items-center gap-1.5 text-xs font-bold text-blue-700 uppercase mb-1.5">
                                                            <Info size={12}/> Range Labels (4, comma separated)
                                                        </label>
                                                        <input
                                                            className="w-full p-2 border border-blue-200 rounded bg-white text-xs"
                                                            placeholder="Struggling, Managing, Performing, Thriving"
                                                            value={q.rangeLabels || ""}
                                                            onChange={(e) => updateQuestion(sIdx, qIdx, 'rangeLabels', e.target.value)}
                                                        />
                                                        <p className="text-[10px] text-slate-500 mt-1 leading-tight">What the player sees for 0-24, 25-49, 50-74 and 75-100. Leave blank for the default. Wording only: the stored answer stays the 0-100 number.</p>
                                                    </div>
                                                )}

                                                {(q.type === 'select' || q.type === 'drag_sort') && (
                                                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 ml-4">
                                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Options</label>
                                                        <div className="space-y-2">
                                                            {(q.options || []).map((opt, optIdx) => (
                                                                <div key={optIdx} className="flex items-center gap-2">
                                                                    <input className="flex-1 p-1 text-xs border border-slate-300 rounded" value={opt} onChange={(e) => updateOption(sIdx, qIdx, optIdx, e.target.value)} />
                                                                    <button onClick={() => removeOption(sIdx, qIdx, optIdx)} className="text-slate-400 hover:text-red-500"><X size={14}/></button>
                                                                </div>
                                                            ))}
                                                            <button onClick={() => addOption(sIdx, qIdx)} className="text-xs text-brand-primary font-bold hover:underline">+ Add Option</button>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-50">
                                                    <div className="flex items-center gap-2">
                                                        <Tag size={14} className="text-slate-400" />
                                                        <select 
                                                            className="text-xs font-bold text-slate-600 bg-slate-50 border border-slate-200 rounded p-1.5 outline-none focus:border-brand-primary"
                                                            value={q.metric || ''}
                                                            onChange={(e) => updateQuestion(sIdx, qIdx, 'metric', e.target.value)}
                                                        >
                                                            <option value="">-- No Score --</option>
                                                            <optgroup label="Performance (Average)">
                                                                {Object.entries(SCORING_METRICS)
                                                                    .filter(([k,v]) => v.type === 'average')
                                                                    .map(([key, val]) => (
                                                                        <option key={key} value={key}>{val.label}</option>
                                                                    ))
                                                                }
                                                            </optgroup>
                                                            <optgroup label="Clinical (Sum)">
                                                                {Object.entries(SCORING_METRICS)
                                                                    .filter(([k,v]) => v.type === 'sum')
                                                                    .map(([key, val]) => (
                                                                        <option key={key} value={key}>{val.label}</option>
                                                                    ))
                                                                }
                                                            </optgroup>
                                                        </select>
                                                    </div>

                                                    <div className="flex-1 flex justify-end">
                                                        {q.trigger ? (
                                                            <div className="flex items-center gap-2 p-1 bg-orange-50 rounded border border-orange-100 text-[10px]">
                                                                <span className="font-bold text-orange-700">LOGIC:</span>
                                                                <select 
                                                                    className="p-0.5 border rounded bg-white font-bold"
                                                                    value={q.trigger.operator || '>='}
                                                                    onChange={(e) => updateTrigger(sIdx, qIdx, 'operator', e.target.value)}
                                                                >
                                                                    <option value=">=">&ge;</option>
                                                                    <option value="<">&lt;</option>
                                                                </select>
                                                                <input type="number" className="w-8 p-0.5 border rounded text-center" value={q.trigger.threshold} onChange={(e) => updateTrigger(sIdx, qIdx, 'threshold', parseInt(e.target.value) || 0)} />
                                                                <select className="p-0.5 border rounded bg-white w-24 truncate" value={q.trigger.module} onChange={(e) => updateTrigger(sIdx, qIdx, 'module', e.target.value)}>
                                                                    {Object.entries(CLINICAL_MODULES).map(([key, module]) => (<option key={key} value={key}>{module.title}</option>))}
                                                                </select>
                                                                <button onClick={() => toggleTrigger(sIdx, qIdx)} className="text-orange-400 hover:text-red-500"><Trash2 size={12}/></button>
                                                            </div>
                                                        ) : (
                                                            <button onClick={() => toggleTrigger(sIdx, qIdx)} className="text-xs text-slate-300 hover:text-brand-primary flex items-center gap-1">
                                                                <AlertTriangle size={12}/> Logic
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {section.questions.length === 0 && <div className="text-center text-xs text-slate-300 italic">No questions yet.</div>}
                            </div>
                        </div>
                    ))}
                    <button onClick={addSection} className="w-full border-2 border-dashed border-slate-200 py-3 text-slate-400 font-bold rounded-xl hover:border-brand-primary hover:text-brand-primary transition-colors">+ Add Section</button>
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <button onClick={startNew} className="flex flex-col items-center justify-center p-8 bg-white border-2 border-dashed border-slate-300 rounded-2xl hover:border-brand-primary hover:bg-blue-50 transition-all group min-h-[200px]">
                <div className="w-16 h-16 bg-blue-100 text-brand-primary rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"><Plus size={32} /></div>
                <h3 className="font-bold text-slate-700 text-lg">Create Blank</h3>
            </button>
            {templates.map(tpl => (
                <div key={tpl.id} className="bg-slate-50 p-6 rounded-2xl shadow-sm border border-slate-200 hover:border-brand-primary/50 transition-all flex flex-col justify-between min-h-[200px] relative overflow-hidden">
                    <div className="absolute top-0 right-0 bg-brand-primary text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl">TEMPLATE</div>
                    <div><div className="flex items-center gap-2 mb-2 text-brand-primary"><LayoutTemplate size={20} /><span className="text-xs font-bold uppercase tracking-wider">Base Layout</span></div><h3 className="font-black text-xl text-slate-800 mb-2">{tpl.title}</h3><p className="text-sm text-slate-500">Ready-to-use structure.</p></div>
                    <button onClick={() => loadTemplate(tpl)} className="w-full mt-6 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg font-bold text-sm hover:bg-brand-primary hover:text-white hover:border-brand-primary transition-colors flex items-center justify-center gap-2"><Copy size={16}/> Use Template</button>
                </div>
            ))}
            {configs.map(config => (
                <div key={config.id} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 hover:shadow-md transition-all flex flex-col justify-between min-h-[200px]">
                    <div>
                        <div className="flex justify-between items-start mb-2"><span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-1 rounded uppercase tracking-wider">Active</span></div>
                        <h3 className="font-black text-xl text-slate-800 mb-2">{config.title}</h3>
                        <p className="text-sm text-slate-500 font-mono bg-slate-50 p-2 rounded truncate">{config.id}</p>
                        <div className="mt-4 space-y-1"><div className="text-xs text-slate-600 flex items-center gap-2"><ListChecks size={14} /> {config.screener?.length || 0} Sections</div></div>
                    </div>
                    <button onClick={() => loadConfig(config)} className="w-full mt-6 py-2 bg-slate-900 text-white rounded-lg font-bold text-sm hover:bg-black transition-colors">Edit Logic</button>
                </div>
            ))}
        </div>
    );
};

export default AssessmentBuilder;